import React, { useRef, useState, useMemo, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { FaHeart } from "react-icons/fa";
import { FaCamera } from "react-icons/fa6";

import styles from "./styles/EditProfile.module.scss";

function buildDisplayName(userInfo) {
  const first = (userInfo.firstname || "").trim();
  const last = (userInfo.lastname || "").trim();
  if (first || last) {
    return [first, last].filter(Boolean).join(" ").trim() || "Admin Panel";
  }
  const raw = (userInfo.name || "").trim();
  if (raw) {
    return raw;
  }
  return "Admin Panel";
}

export default function EditProfile() {
  const userInfo = useSelector((state) => state.auth.userInfo);
  const fileRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [remoteImgFailed, setRemoteImgFailed] = useState(false);

  const defaults = useMemo(() => {
    const first = (userInfo.firstname || "").trim();
    const last = (userInfo.lastname || "").trim();
    if (first || last) {
      return { firstName: first || "Admin", lastName: last || "Panel" };
    }
    const name = (userInfo.name || "").trim();
    if (name) {
      const parts = name.split(/\s+/);
      return {
        firstName: parts[0] || "Admin",
        lastName: parts.slice(1).join(" ") || "Panel",
      };
    }
    return { firstName: "Admin", lastName: "Panel" };
  }, [userInfo]);

  const { handleSubmit, register, reset } = useForm({
    defaultValues: {
      firstName: defaults.firstName,
      lastName: defaults.lastName,
    },
  });

  useEffect(() => {
    reset({
      firstName: defaults.firstName,
      lastName: defaults.lastName,
    });
  }, [defaults.firstName, defaults.lastName, reset]);

  const displayName = buildDisplayName(userInfo);
  const email = userInfo.email?.trim() || "hi-admin@yopmail.com";
  const remoteImage =
    userInfo.profile_image || userInfo.image_url || userInfo.avatar;

  useEffect(() => {
    setRemoteImgFailed(false);
  }, [remoteImage]);

  const handlePickPhoto = () => fileRef.current?.click();

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  };

  const onSave = () => {
    // Wire to API when available
  };

  return (
    <div className={styles.EditProfile}>
      <h2 className={styles.heading}>Edit Profile</h2>
      <p className={styles.sub}>
        Update your display name and photo to personalize your admin account.
      </p>
      <Form onSubmit={handleSubmit(onSave)}>
        <div className={styles.form}>
          <div className={styles.avatarBlock}>
            <div className={styles.avatar}>
              {previewUrl ? (
                <img src={previewUrl} alt="" />
              ) : remoteImage && !remoteImgFailed ? (
                <img
                  src={remoteImage}
                  alt=""
                  onError={() => setRemoteImgFailed(true)}
                />
              ) : (
                <FaHeart aria-hidden />
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className={styles.hiddenFile}
              onChange={onFile}
            />
            <button
              type="button"
              className={styles.editFab}
              onClick={handlePickPhoto}
              aria-label="Change profile photo"
            >
              <FaCamera size={16} />
            </button>
          </div>

          <p className={styles.displayName}>{displayName}</p>
          <p className={styles.email}>{email}</p>

          <div className={styles.field}>
            <Form.Control
              className={styles.input}
              placeholder="First name"
              {...register("firstName")}
            />
          </div>
          <div className={styles.field}>
            <Form.Control
              className={styles.input}
              placeholder="Last name"
              {...register("lastName")}
            />
          </div>

          <button type="submit" className={styles.saveBtn}>
            Save
          </button>
        </div>
      </Form>
    </div>
  );
}
